import { useCallback, useState } from "react";

import { useEditPlayMutation } from "../../../redux/slices";
import { SportPlay } from "../../../types";
import { BasePlayDataErrors, validatePlayData } from "../../../utils";

export type UpdatePlayCallbackFunc = <TPlay extends SportPlay>(play: TPlay) => TPlay;
export type UpdatePlayCallback<TPlay extends SportPlay> = UpdatePlayCallbackFunc | TPlay;

export interface UseEditPlayProps<TPlay extends SportPlay> {
  onSave?: (play: TPlay) => void;
}

export const useEditPlay = <TPlay extends SportPlay>({ onSave }: UseEditPlayProps<TPlay> = {}) => {
  // play being edited
  const [editingPlay, setEditingPlay] = useState<TPlay>();

  // update play and perform undefined check. input param is same as setEditingPlay, can be TPlay or callback function
  // containing prior state
  const updatePlay = useCallback((updatePlayCallback: UpdatePlayCallback<TPlay>) => {
    setEditingPlay((prevValue) => {
      if (!prevValue) return prevValue;
      return updatePlayCallback instanceof Function ? updatePlayCallback(prevValue) : updatePlayCallback;
    });
  }, []);

  // show edit play dialog by setting editingPlay
  const showEditPlay = (play: TPlay | undefined) => {
    setEditingPlay(play);
  };

  // close edit play dialog by setting editingPlay to undefined
  const closeEditPlay = () => {
    setEditingPlay(undefined);
  };

  /** Edit Play Mutation */
  const { editPlay } = useEditPlayMutation();
  const [errors, setErrors] = useState<BasePlayDataErrors>({});
  const savePlay = () => {
    // validate play data
    const errors = validatePlayData(editingPlay);

    // get num of errors
    const errorCount = Object.keys(errors).length;

    // if no errors, save play
    if (errorCount === 0 && editingPlay) {
      // save play
      editPlay({ play: editingPlay });
      // reset errors and close dialog
      setErrors({});
      setEditingPlay(undefined);
      // if onSave sideEffect, pass new play to it
      onSave && onSave(editingPlay);
    }
    // else, set errors
    else {
      setErrors(errors);
    }
  };

  return { editingPlay, setEditingPlay, updatePlay, showEditPlay, closeEditPlay, savePlay, errors };
};

export type UseEditPlay<TPlay extends SportPlay> = ReturnType<typeof useEditPlay<TPlay>>;
